import axios from 'axios';

import {
  Configuration,
  DiscountV4ControllerApiFactory,
} from '@ce-openapi/moka-inventory-discount-service-ts-axios-client';
import { MOKA_INVENTORY_API } from 'mf-shared/api/constants';
import {
  CREATE_ALL_OUTLET,
  DELETE_DISCOUNT,
  DELETE_DISCOUNT_OUTLETS,
  DISCOUNT_OUTLETS_DETAIL,
  GET_DISCOUNT_OUTLETS_LIST,
  PATCH_DISCOUNT_OUTLETS,
  POST_DISCOUNT,
  POST_DISCOUNT_OUTLETS,
  PUT_DISCOUNT,
} from './constants';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const reject = (error, name?: string) => {
  let eobj;

  if (error.response) {
    const { data } = error.response;

    if (data.code && data.code === '0005-0001') {
      eobj = new Error(`Discount does not found or already removed`);
    } else if (data.code && data.code === '0005-0010') {
      eobj = new Error(
        `Discount name already exists in your business, please use another name.`
      );
    } else if (data.errors) {
      eobj = new Error(data.errors);
    } else if (data.message) {
      eobj = new Error(data.message);
    } else {
      eobj = new Error(error.message);
    }
  } else {
    eobj = new Error(error.message);
  }

  return Promise.reject(eobj);
};

export interface Discount {
  name: string;
  amount: number | null;
  type: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  amount_format: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  business_id: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  outlet_ids: number[];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  total_outlets: number;
}

interface DiscountParams {
  readonly cursor?: string;
  readonly perPage?: number;
  readonly outletIds?: (string | number)[];
}

export function getCustomDiscountsApi({
  cursor: currentCursor,
  perPage,
  outletIds,
}: DiscountParams) {
  return DiscountV4ControllerApiFactory(
    new Configuration({
      basePath: MOKA_INVENTORY_API,
    })
  )
    .listDiscounts(
      undefined,
      undefined,
      outletIds?.length === 1 ? Number(outletIds[0]) : undefined,
      currentCursor,
      perPage,
      false,
      undefined,
      undefined
    )
    .then(
      ({
        data: { results, next_cursor: cursor, is_completed: completed },
      }) => ({
        results,
        cursor,
        completed,
      })
    );
}

/**
 * @see https://github.com/mokapos/moka-rails/blob/master/app/controllers/api/v2/discounts_controller.rb#L5
 * @param params
 * @return {Axios.IPromise<U>}
 */
export function getDiscounts({
  page,
  per_page: perPage,
  outletIds,
}: {
  page: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  per_page: number;
  outletIds?: (string | number)[];
}) {
  return (
    axios
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .get<{ results: Discount[]; total_pages: number; total_counts: number }>(
        GET_DISCOUNT_OUTLETS_LIST,
        {
          withCredentials: true,
          params: {
            page,
            perPage,
          },
          ...(outletIds?.length === 1
            ? // eslint-disable-next-line @typescript-eslint/naming-convention
              { headers: { outlet_id: outletIds } }
            : {}),
        }
      )
      .catch(reject)
  );
}

export function postDiscount(data, params) {
  return axios
    .post(POST_DISCOUNT, data, { params, withCredentials: true })
    .catch(reject);
}

export function putDiscount(id, data, params) {
  return axios
    .put(PUT_DISCOUNT(id), data, { params, withCredentials: true })
    .catch((error) => reject(error, data.name));
}

export function deleteDiscount(id, name, params) {
  return axios
    .delete(DELETE_DISCOUNT(id), { params, withCredentials: true })
    .catch((error) => reject(error, name));
}

export function postCreateAllOutlet(data, params) {
  return axios
    .post(CREATE_ALL_OUTLET, data, { params, withCredentials: true })
    .catch(reject);
}

export function getDiscountOutletsDetail(params) {
  return axios
    .get(DISCOUNT_OUTLETS_DETAIL, { withCredentials: true, params })
    .catch(reject);
}

export function postCreateDiscountWithOutlets(
  data: Omit<Discount, 'total_outlets' | 'amount_format'>
) {
  return axios
    .post(POST_DISCOUNT_OUTLETS, data, { withCredentials: true })
    .catch(reject);
}

export function updateDiscountWithOutlets(data) {
  return axios
    .patch(PATCH_DISCOUNT_OUTLETS, data, { withCredentials: true })
    .catch(reject);
}

export function deleteDiscountWithOutlets(data) {
  return axios
    .delete(DELETE_DISCOUNT_OUTLETS, { data, withCredentials: true })
    .catch(reject);
}
